import { Link } from "react-router-dom";
import type { Movie } from "@/types";
import StarRating from "@/components/base/StarRating";

interface DetailHeroProps {
  movie: Movie;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  onShare: () => void;
  isNewFavorite: boolean;
}

export default function DetailHero({
  movie,
  isFavorite,
  onToggleFavorite,
  onShare,
  isNewFavorite,
}: DetailHeroProps) {
  return (
    <section className="relative overflow-hidden bg-primary-950">
      <div className="absolute inset-0">
        <img
          src={movie.cover}
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover object-top opacity-30 blur-sm"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-primary-950/60"></div>
      </div>

      <div className="relative mx-auto flex max-w-[1180px] flex-col gap-6 px-4 py-8 md:flex-row md:px-8 md:py-12">
        <div className="w-40 shrink-0 self-center sm:w-48 md:self-start">
          <div className="overflow-hidden rounded-lg border border-primary-800">
            <img
              src={movie.cover}
              alt={`${movie.title} 封面海报 - 棒棒TV影视详情`}
              title={`${movie.title} - 棒棒TV`}
              className="aspect-[2/3] w-full object-cover object-top"
            />
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {movie.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-md bg-secondary-100 px-2.5 py-1 text-[11px] font-semibold text-secondary-800"
              >
                {tag}
              </span>
            ))}
          </div>

          <h1 className="mt-3 font-heading text-2xl font-black text-background-50 md:text-4xl">
            {movie.title}
          </h1>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-200">
            <StarRating value={movie.rating} size="md" />
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {movie.releaseDate}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-map-pin-line"></i>
              {movie.region}
            </span>
            <span className="flex items-center gap-1 text-accent-500">
              <i className="ri-fire-line"></i>
              热度 {(movie.hotness / 1000).toFixed(1)}k
            </span>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {movie.genres.map((genre) => (
              <Link
                key={genre}
                to={`/category/${movie.type}`}
                className="rounded-md border border-background-100/25 px-2.5 py-1 text-xs text-background-200 transition-colors hover:border-accent-500 hover:text-accent-500"
              >
                {genre}
              </Link>
            ))}
          </div>

          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-300">
            {movie.synopsis}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={onToggleFavorite}
              className={`flex items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-semibold transition-colors cursor-pointer ${
                isFavorite
                  ? "bg-accent-500 text-background-50"
                  : "border border-background-100/30 text-background-50 hover:bg-background-50/10"
              }`}
            >
              <i className={`${isFavorite ? "ri-bookmark-3-fill" : "ri-bookmark-3-line"} text-lg`}></i>
              {isFavorite ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={onShare}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-100/30 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer"
            >
              <i className="ri-share-forward-line text-lg"></i>
              分享
            </button>
            {isNewFavorite && (
              <span className="animate-fade text-xs text-secondary-300">
                <i className="ri-checkbox-circle-line mr-1"></i>
                已保存到本地收藏
              </span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}